import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FieldValueInterface,
  FormInputComponent,
  InlineEditService,
  ObjectType,
  StandardIds,
  SyncIconComponent,
} from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { tap, of, map, switchMap, Observable, startWith, catchError, combineLatest } from 'rxjs';
import {
  PartnerCRMSalespersonService,
  salespersonFilterFn,
  SalespersonWithDisplayName,
} from '../../salesperson.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';

const NO_VALUE_FOR_SALESPERSON = '<NO_VALUE_FOR_SALESPERSON>';

@Component({
  templateUrl: './salesperson-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatIcon,
    MatIconButton,
    SyncIconComponent,
  ],
  styleUrls: ['../shared.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SalespersonFormInputComponent extends FormInputComponent implements OnInit {
  NO_VALUE_FOR_SALESPERSON = NO_VALUE_FOR_SALESPERSON;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<SalespersonWithDisplayName | string> = new FormControl<SalespersonWithDisplayName | string>(
    '',
    {
      nonNullable: true,
    },
  );
  _group: UntypedFormGroup;
  _label = '';
  searchTerm = signal<SalespersonWithDisplayName | string>('');

  private isSalespersonValid(control: AbstractControl): ValidationErrors | null {
    const salespersonId = this.extractSalespersonIDFromValue(control.value);
    const validSalesperson = !!this.availableOptions().find((s) => s.salespersonId === salespersonId);
    if (validSalesperson || !salespersonId) {
      return null;
    }
    return {
      invalidSalesperson: true,
    };
  }

  set control(control: FormControl<SalespersonWithDisplayName | string>) {
    this._control = control;
    this._control.clearValidators();
    this._control.addValidators(this.isSalespersonValid.bind(this));
  }

  get control(): UntypedFormControl {
    return this._control;
  }

  private getAdditionalSalespeopleFieldId(): string {
    if (this.objectType === 'Contact') {
      return StandardIds.ContactAdditionalSalespersonIDs;
    } else {
      return StandardIds.CompanyAdditionalSalespersonIDs;
    }
  }

  set formGroup(group: UntypedFormGroup) {
    this._group = group;
    group.addValidators((control) => {
      const salespersonControl = control.get(this._fieldId());
      const path = this.getAdditionalSalespeopleFieldId();
      const additionalSalespeopleControl = control.get(path);
      if (salespersonControl && additionalSalespeopleControl) {
        const salespersonValue: string | SalespersonWithDisplayName = salespersonControl.value;
        const salespersonID = typeof salespersonValue === 'string' ? salespersonValue : salespersonValue?.salespersonId;
        const additionalSalespeopleValue: string[] = additionalSalespeopleControl.value;
        if (salespersonValue && additionalSalespeopleValue) {
          if (additionalSalespeopleValue.includes(salespersonID)) {
            return {
              salespersonInAdditionalSalespeople: true,
            };
          }
        }
      }
      return null;
    });
  }

  get formGroup(): UntypedFormGroup {
    return this._group;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    const controlValue: SalespersonWithDisplayName | string = this._control.value;
    if (typeof controlValue === 'string') {
      return {
        fieldId: this._fieldId(),
        stringValue: controlValue,
      };
    }
    return {
      fieldId: this._fieldId(),
      stringValue: controlValue?.salespersonId,
    };
  }

  loadingValues = signal(true);
  currentSelectedSalesperson = signal<SalespersonWithDisplayName | null>(null);
  availableOptions = signal<SalespersonWithDisplayName[]>([]);
  filteredOptions = [] as SalespersonWithDisplayName[];
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  salespersonName = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.salespersonId;
    if (!searchTerm) {
      return NO_VALUE_FOR_SALESPERSON;
    }
    const foundSalesperson = availableOptions.find((salesperson) => salespersonFilterFn(salesperson, filterTerm));
    return foundSalesperson?.displayName || searchTerm;
  });

  private extractSalespersonIDFromValue(value: SalespersonWithDisplayName | string): string {
    let salespersonID: string;
    if (value && typeof value === 'object') {
      salespersonID = value.salespersonId;
    } else if (typeof value === 'string') {
      salespersonID = value;
    }
    return salespersonID;
  }

  private currentSalesperson$(): Observable<SalespersonWithDisplayName | null> {
    return this._control.valueChanges.pipe(
      startWith(this._control.value),
      distinctUntilChanged(),
      switchMap((v) => {
        const salespersonId = this.extractSalespersonIDFromValue(v);
        if (!salespersonId) {
          return of(null);
        }
        return this.salespersonService.getMultiSalesperson([salespersonId]).pipe(
          map((results) => {
            if (results.length === 0 || !results[0]?.salespersonId) {
              return null;
            }
            return results[0];
          }),
        );
      }),
      catchError(() => {
        return of(null);
      }),
      shareReplay(1),
    );
  }

  constructor(
    private readonly salespersonService: PartnerCRMSalespersonService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
  }

  ngOnInit(): void {
    combineLatest([this.salespersonService.allSalespeople$, this.currentSalesperson$()])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(([allSalespeople, currentSelectedSalesperson]) => {
          this.availableOptions.set(allSalespeople);
          this.currentSelectedSalesperson.set(currentSelectedSalesperson);
          this._control.updateValueAndValidity();
          this.loadingValues.set(false);
          // force value to run displayWith function from mat-autocomplete in template
          this._control.setValue(this._control.value);
        }),
      )
      .subscribe();
  }

  clear(): void {
    this._control.reset();
    this.searchTerm.set('');
  }

  displayFn(salesperson: SalespersonWithDisplayName | string): string {
    if (this.currentSelectedSalesperson()?.displayName) {
      return this.currentSelectedSalesperson().displayName;
    }
    if (!salesperson) {
      return '';
    }
    if (typeof salesperson === 'string') {
      const foundSalesperson = this.availableOptions().find((sp) => sp.salespersonId === salesperson);
      return foundSalesperson?.displayName || salesperson;
    }
    return salesperson?.displayName;
  }

  filter(): void {
    this.filteredOptions = this.availableOptions().filter((salesperson) =>
      salespersonFilterFn(salesperson, this.input.nativeElement.value),
    );
  }

  saveField(): void {
    if (!this._control.errors && !this._group.errors?.salespersonInAdditionalSalespeople) {
      // attempt saving additional salespeople as well, if there are no changes it won't save
      this.inlineEditService.saveInlineEdit(this.objectType, [
        this.value,
        {
          fieldId: this.getAdditionalSalespeopleFieldId(),
          stringValues: {
            values: this._group?.value[this.getAdditionalSalespeopleFieldId()],
          },
        },
      ]);
    }
  }
}
